import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FolderOpen, Package, Users, UserPlus } from 'lucide-react';
import { useGlobalSearch } from '../hooks/useSearch.js';

const typeConfig: Record<string, { icon: typeof FolderOpen; label: string; kleur: string }> = {
  dossier: { icon: FolderOpen, label: 'Dossiers', kleur: 'text-blue-500' },
  apparaat: { icon: Package, label: 'Apparaten', kleur: 'text-green-500' },
  contact: { icon: Users, label: 'Contacten', kleur: 'text-amber-500' },
  lead: { icon: UserPlus, label: 'Leads', kleur: 'text-purple-500' },
};

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useGlobalSearch(query);

  // Focus input wanneer overlay opent
  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Escape sluit
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // Groepeer resultaten per type
  interface SearchResultItem { type: string; id: string; title: string; subtitle: string; meta: string; linkTo: string }
  const grouped = new Map<string, SearchResultItem[]>();
  for (const r of (data?.results || []) as SearchResultItem[]) {
    if (!grouped.has(r.type)) grouped.set(r.type, []);
    grouped.get(r.type)!.push(r);
  }

  const handleSelect = (linkTo: string) => {
    navigate(linkTo);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Search panel */}
      <div className="relative bg-white rounded-xl w-full max-w-xl shadow-2xl border border-gray-200 overflow-hidden mx-4">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search size={20} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Zoek dossier, klant, apparaat..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 text-sm outline-none bg-transparent"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-300 hover:text-gray-500">
              <X size={16} />
            </button>
          )}
          <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Resultaten */}
        <div className="max-h-[400px] overflow-y-auto">
          {query.length < 2 ? (
            <div className="px-4 py-10 text-center">
              <Search size={32} className="text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Begin met typen om te zoeken</p>
              <p className="text-xs text-gray-300 mt-1">Doorzoek dossiers, apparaten, contacten en leads</p>
            </div>
          ) : isLoading ? (
            <div className="px-4 py-10 text-center">
              <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-400">Zoeken...</p>
            </div>
          ) : data && data.results.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-sm text-gray-500">Geen resultaten voor &quot;{query}&quot;</p>
              <p className="text-xs text-gray-400 mt-1">Probeer een andere zoekterm</p>
            </div>
          ) : (
            <div className="py-2">
              {Array.from(grouped.entries()).map(([type, items]) => {
                const config = typeConfig[type] || typeConfig.dossier;
                const Icon = config.icon;
                return (
                  <div key={type}>
                    {/* Groep header */}
                    <div className="flex items-center gap-2 px-4 py-1.5">
                      <Icon size={14} className={config.kleur} />
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{config.label}</span>
                      <span className="text-[10px] text-gray-300">{items.length}</span>
                    </div>

                    {/* Items */}
                    {items.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        onClick={() => handleSelect(r.linkTo)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                          {r.subtitle && (
                            <p className="text-xs text-gray-500 truncate">{r.subtitle}</p>
                          )}
                        </div>
                        {r.meta && (
                          <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                            {r.meta}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {data && data.results.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">{data.totalCount} resultaten</span>
            <span className="text-[11px] text-gray-300">Click om te navigeren</span>
          </div>
        )}
      </div>
    </div>
  );
}
